// stores/templates/support.ejs
import { type StateTree } from "pinia";

export function cacheSerialize(value: StateTree): string {
  return JSON.stringify(value);
}
export function cacheDeserialize(value: string): StateTree {
  return new Map(JSON.parse(value));
}

import { computed, Ref } from "vue";
//import { useEventBus } from '@vueuse/core'
//import { Semaphore } from 'async-mutex'
import { uniq } from "lodash-es";
import { isIri } from "@/helpers/iri";
import { fromPairs } from "lodash";
export type AlsoRef<T> = T | Ref<T>;
export type Updateable<T> = Partial<
  Omit<{ -readonly [P in keyof T]-?: T[P] }, "@id" | "@context" | "@type">
>;
export function checkIriArray(
  iris: string[],
  iriPrefix?: string,
): { inputCnt: number; outputCnt: number; iris: string[] } {
  if (iris.length === 0) return { inputCnt: 0, outputCnt: 0, iris: [] };
  iris = uniq(iris); //silently ensure uniqueness
  iris = iris.filter((iri) => !!iri); //silenty filter out undefined
  if (iris.length === 0) return { inputCnt: 0, outputCnt: 0, iris: [] };
  const inCnt = iris.length; //get input count
  iris = iris.filter((iri) => isIri(iri, iriPrefix));
  const iriCnt = iris.length;
  // if (iriCnt != iris.length)
  //   logger.warn(
  //     `At least one given iri is not valid or does not match api prefix (${this.$iriPrefix})!`,
  //     iris,
  //   );
  return { inputCnt: inCnt, outputCnt: iriCnt, iris };
}
export function HydraToIriDictionary<T extends JsonldObject>(
  jsonld: Ref<JsonldCollection<T>>,
): Ref<Record<string, T>> {
  return computed(() => {
    const items = (jsonld.value?.["hydra:member"] ?? []) as JsonldObject[];
    const dict: Record<string, JsonldObject> = {};
    for (const item of items) {
      if (item && item["@id"]) dict[item["@id"]] = item;
    }
    return dict as Record<string, T>;
  });
}
export function HydraToIriMap<T extends JsonldObject>(
  jsonld: Ref<JsonldCollection<T>>,
): Ref<Map<string, T>> {
  return computed(() => {
    return new Map(Object.entries(HydraToIriDictionary(jsonld)));
  });
}
export function HydraToPagination<T extends JsonldObject>(
  jsonld: Ref<JsonldCollection<T>>,
  query?: { itemsPerPage?: number; page?: number },
): Ref<Pagination<T>> {
  // const view = jsonld['hydra:view']
  return computed(() => ({
    data: jsonld.value?.["hydra:member"] ?? [],
    totalItems: jsonld.value?.["hydra:totalItems"] ?? -1,
    totalPages: jsonld.value?.["hydra:totalItems"]
      ? Math.ceil(
          jsonld.value?.["hydra:totalItems"] / (query?.itemsPerPage || 100),
        )
      : 0,
    hasNextPage:
      query?.page && jsonld.value?.["hydra:totalItems"]
        ? query.page <
          Math.ceil(
            jsonld.value?.["hydra:totalItems"] / (query.itemsPerPage || 100),
          )
        : false,
    hasPrevPage: query?.page ? query.page > 1 : false,
    currentPage: query?.page ?? 1,
    itemsPerPage: query?.itemsPerPage ?? 100,
  }));
}
export async function filterAsync<T>(
  arr: T[],
  predicate: (value: T, index: number, array: T[]) => Promise<unknown>,
) {
  return Promise.all(arr.map<Promise<unknown>>(predicate)).then((results) =>
    arr.filter((_v, index) => results[index]),
  );
}
export async function arrayToObject<T, K extends string, V>(
  array: T[],
  keyCallback: (item: T) => K,
  valueCallback: (item: T) => Promise<V>,
): Promise<Record<K, V>> {
  const entries = await Promise.all(
    array.map(
      async (item) => [keyCallback(item), await valueCallback(item)] as const,
    ),
  );
  return fromPairs(entries as [K, Awaited<V>][]) as Record<K, V>;
}
export interface Pagination<T> {
  data: T[];
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}
export interface JsonldObject {
  "@id": string;
  [key: string]: unknown;
}
export interface JsonldCollection<T extends JsonldObject> {
  [key: string]: unknown;
  "hydra:member": T[];
  "hydra:totalItems"?: number;
  "hydra:view"?: {
    "@id"?: string;
    "@type"?: string;
    "hydra:first"?: string;
    "hydra:last"?: string;
    "hydra:previous"?: string;
    "hydra:next"?: string;
  };
  "hydra:search"?: {
    "@type"?: string;
    "hydra:template"?: string;
    "hydra:variableRepresentation"?: string;
    "hydra:mapping"?: {
      "@type"?: string;
      variable?: string;
      property?: string | null;
      required?: boolean;
    }[];
  };
}
export interface RequestOptions<ThrowGlobalError extends boolean = true> {
  forceRefresh?: boolean;
  context?: string;
  errorOnIncompleteCollection?: boolean;
  throwGlobalError?: ThrowGlobalError;
}
export function defaultRequestOptionsMerge<ThrowGlobalError extends boolean>(
  override?: Partial<RequestOptions<ThrowGlobalError>>,
): RequestOptions<ThrowGlobalError> {
  return {
    throwGlobalError: override?.throwGlobalError ?? (true as ThrowGlobalError),
    ...override,
  };
}
export interface CacheEntry<T> {
  expires: Date | string;
  data: T;
}
export function storeMethodArgumentsCompatibilityLayer<
  ThrowGlobalError extends boolean = true,
>(
  funcArgs: unknown[],
  scope:
    | "delete"
    | "fetchAllByIri"
    | "fetchAllByIriAsDictionary"
    | "fetchAllByIriAsMap"
    | "find"
    | "findAll"
    | "findAllAsIriDictionary"
    | "findAllWithPagination"
    | "findAllAsMap"
    | "fetchBy"
    | "fetchByIri"
    | "patch"
    | "post"
    | "put",
): RequestOptions<ThrowGlobalError> | undefined {
  if (
    funcArgs.length == 1 ||
    typeof funcArgs[1] === "object" ||
    typeof funcArgs[1] === "undefined"
  )
    return (funcArgs[1] ?? undefined) as
      | RequestOptions<ThrowGlobalError>
      | undefined; //only payload or already object given -> no need to convert anything
  console.warn(
    "Found deprecated store method call signature - please fix (see trace for location)",
  );
  switch (scope) {
    case "fetchAllByIri":
    case "fetchAllByIriAsDictionary":
    case "fetchAllByIriAsMap":
      return {
        forceRefresh: funcArgs[1] as boolean | undefined,
        errorOnIncompleteCollection: funcArgs[2] as boolean | undefined,
        context: funcArgs[3] as string | undefined,
      };
    case "findAll":
    case "findAllAsIriDictionary":
    case "findAllWithPagination":
    case "findAllAsMap":
    case "fetchBy":
    case "fetchByIri":
    case "find":
      return {
        forceRefresh: funcArgs[1] as boolean | undefined,
        context: funcArgs[2] as string | undefined,
      };
    case "patch":
    case "post":
    case "put":
    case "delete":
      //had no options to begin with - no reason to change anything
      return undefined;
  }
}
